import React, { useRef, useState } from "react";

export function Magnetic({ children, strength = 0.5 }: { children: React.ReactNode, strength?: number }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);
  // Cache the element rect for the whole hover session. Reading it on every
  // mousemove forces a synchronous layout after each setPosition invalidates
  // styles -- a classic forced-reflow loop. The rect is read once (first move
  // of a hover) and dropped on leave, when a fresh read is free anyway.
  const rectRef = useRef<DOMRect | null>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const { clientX, clientY } = e;
    const rect = (rectRef.current ??= ref.current.getBoundingClientRect());
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setPosition({
      x: (clientX - centerX) * strength,
      y: (clientY - centerY) * strength,
    });
  };

  const handleMouseLeave = () => {
    rectRef.current = null;
    setPosition({ x: 0, y: 0 });
  };

  const isMobile = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
  if (isMobile) return <>{children}</>;

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        transition: position.x === 0 && position.y === 0 ? 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'transform 0.08s linear',
        willChange: 'transform',
      }}
    >
      {children}
    </div>
  );
}
